import { SHEET_NAME as DETAILS_OF_SIA_SHEET } from "./detailsOfSIASheet";
import { SHEET_NAME as DETAILS_OF_BENEFITS_FROM_IP_SHEET } from "./detailsOfBenefitsFromIPSheet";
import { SHEET_NAME as DETAILS_OF_CSR_PROJECT_SHEET } from "./detailsOfCSRProjectSheet";
import { SHEET_NAME as DETAILS_OF_CORRECTIVE_ACTION_IP_SHEET } from "./detailsOfCorrectiveActionIPSheet";
import { SHEET_NAME as PROJECT_FOR_REHABILITATION_SHEET } from "./projectForRehabilitationSheet";
import { SHEET_NAME as ACTION_TO_MITIGATE_NEGATIVE_SIA_SHEET } from "./actionToMitigateNegativeSIASheet";
import { SHEET_NAME as CSR_IN_ASPIRATIONAL_DISTRICTS_SHEET } from "./csrInAspirationalDistrictsSheet";
import {
  SUBHEADER_FILL,
  addParamFyHeader,
  addParamFyRow,
  addQuestionRow,
  addSectionHeader,
  addShortAnswerRow,
  addTextAnswerRow,
  formatPercent,
  getFyLabels,
  internalSheetLink,
  mergeRow,
  setPrinciple5ColumnWidths,
  toYesNo,
  COL_D,
  COL_N,
} from "./principle6SheetHelpers";
import { readLocationFY } from "./principle8SheetHelpers";

const PRINCIPLE_TITLE =
  "Businesses should promote inclusive growth and equitable development";

const SUPPLIER_ROWS = [
  ["Directly sourced from MSMEs/ small producers", "smallProducers"],
  [
    "Sourced directly from within the district and neighbouring districts",
    "neighbouringDistricts",
  ],
];

const JOB_CREATION_ROWS = [
  ["1. Rural", "rural"],
  ["2. Semi-urban", "semiUrban"],
  ["3. Urban", "urban"],
  ["4. Metropolitan", "metropolitan"],
];

function addLocationPercentTable(ws, row, title, rows, source, fyCurrent, fyPrevious) {
  row = addQuestionRow(ws, row, title, "", { fullWidth: true });
  row = addParamFyHeader(ws, row, fyCurrent, fyPrevious);

  rows.forEach(([label, key]) => {
    const values = readLocationFY(source?.[key]);
    row = addParamFyRow(ws, row, label, values.current, values.previous);
  });

  return row;
}

function addJobCreationBlock(ws, row, jobCreation, fyCurrent, fyPrevious) {
  row = addQuestionRow(
    ws,
    row,
    "5. Job creation in smaller towns - Disclose wages paid to persons employed (including employees or workers employed on a permanent or non permanent / on contract basis) in the following locations, as % of total wage cost:",
    "",
    { fullWidth: true }
  );
  row = addParamFyHeader(ws, row, fyCurrent, fyPrevious);

  JOB_CREATION_ROWS.forEach(([sectionLabel, key]) => {
    mergeRow(ws, row, COL_D, COL_N, sectionLabel, {
      bold: true,
      fill: SUBHEADER_FILL,
      align: "left",
    });
    row += 1;

    const values = readLocationFY(jobCreation?.[key]);
    row = addParamFyRow(
      ws,
      row,
      `Disclose wages paid to persons employed in ${sectionLabel.replace(/^\d+\.\s*/, "")} areas`,
      values.current,
      values.previous
    );
  });

  return row;
}

function procurementYesNo(action) {
  if (action === true || action === "true" || action === "yes" || action === "Yes") return "Yes";
  if (action === false || action === "false" || action === "no" || action === "No") return "No";
  return toYesNo(action);
}

export function buildPrinciple8Sheet(workbook, sectionC, reportingYear = "") {
  const ws = workbook.addWorksheet("PRINCIPLE 8", { views: [{ showGridLines: true }] });
  setPrinciple5ColumnWidths(ws);

  const principle = sectionC?.principle8 || {};
  const essential = principle?.essentialIndicators || {};
  const leadership = principle?.leadershipIndicator || {};
  const procurement = leadership?.procurementPolicy || {};
  const { current: fyCurrent, previous: fyPrevious } = getFyLabels(reportingYear);

  let row = 3;
  row = addSectionHeader(ws, row, `PRINCIPLE 8: ${PRINCIPLE_TITLE}`);
  row = addSectionHeader(ws, row, "Essential Indicators", SUBHEADER_FILL);

  row = addQuestionRow(
    ws,
    row,
    "1. Details of Social Impact Assessments (SIA) of projects undertaken by the entity based on applicable laws, in the current financial year.",
    "Show Detail",
    {
      link: internalSheetLink(DETAILS_OF_SIA_SHEET),
      valueAlign: "center",
      valueVertical: "middle",
    }
  );

  row = addQuestionRow(
    ws,
    row,
    "2. Provide information on project(s) for which ongoing Rehabilitation and Resettlement (R&R) is being undertaken by your entity, in the following format:",
    "Show Detail",
    {
      link: internalSheetLink(PROJECT_FOR_REHABILITATION_SHEET),
      valueAlign: "center",
      valueVertical: "middle",
    }
  );

  row = addTextAnswerRow(
    ws,
    row,
    "3. Describe the mechanisms to receive and redress grievances of the community.",
    essential?.communityGrievanceMechanisms
  );

  row = addLocationPercentTable(
    ws,
    row,
    "4. Percentage of input material (inputs to total inputs by value) sourced from suppliers:",
    SUPPLIER_ROWS,
    essential?.sourceFromSupplier,
    fyCurrent,
    fyPrevious
  );

  row = addJobCreationBlock(ws, row, essential?.jobCreationInSmallerTowns, fyCurrent, fyPrevious);

  row = addSectionHeader(ws, row, "Leadership Indicators", SUBHEADER_FILL);

  row = addQuestionRow(
    ws,
    row,
    "1. Provide details of actions taken to mitigate any negative social impacts identified in the Social Impact Assessments (Reference: Question 1 of Essential Indicators above):",
    "Show Detail",
    {
      link: internalSheetLink(ACTION_TO_MITIGATE_NEGATIVE_SIA_SHEET),
      valueAlign: "center",
      valueVertical: "middle",
    }
  );

  row = addQuestionRow(
    ws,
    row,
    "2. Provide the following information on CSR projects undertaken by your entity in designated aspirational districts as identified by government bodies:",
    "Show Detail",
    {
      link: internalSheetLink(CSR_IN_ASPIRATIONAL_DISTRICTS_SHEET),
      valueAlign: "center",
      valueVertical: "middle",
    }
  );

  row = addShortAnswerRow(
    ws,
    row,
    "3. (a) Do you have a preferential procurement policy where you give preference to purchase from suppliers comprising marginalized / vulnerable groups? (Yes / No)",
    procurementYesNo(procurement?.action)
  );

  if (procurementYesNo(procurement?.action) === "Yes") {
    row = addTextAnswerRow(
      ws,
      row,
      "3. (b) From which marginalized / vulnerable groups do you procure?",
      procurement?.vulnerableGoupProcure
    );
    row = addShortAnswerRow(
      ws,
      row,
      "3. (c) What percentage of total procurement (by value) does it constitute?",
      formatPercent(procurement?.totalProcurementByValue)
    );
  } else {
    row = addTextAnswerRow(
      ws,
      row,
      "3. (b) From which marginalized / vulnerable groups do you procure?",
      ""
    );
    row = addShortAnswerRow(
      ws,
      row,
      "3. (c) What percentage of total procurement (by value) does it constitute?",
      ""
    );
  }

  row = addQuestionRow(
    ws,
    row,
    "4. Details of the benefits derived and shared from the intellectual properties owned or acquired by your entity (in the current financial year), based on traditional knowledge:",
    "Show Detail",
    {
      link: internalSheetLink(DETAILS_OF_BENEFITS_FROM_IP_SHEET),
      valueAlign: "center",
      valueVertical: "middle",
    }
  );

  row = addQuestionRow(
    ws,
    row,
    "5. Details of corrective actions taken or underway, based on any adverse order in intellectual property related disputes wherein usage of traditional knowledge is involved.",
    "Show Detail",
    {
      link: internalSheetLink(DETAILS_OF_CORRECTIVE_ACTION_IP_SHEET),
      valueAlign: "center",
      valueVertical: "middle",
    }
  );

  row = addQuestionRow(
    ws,
    row,
    "6. Details of beneficiaries of CSR Projects:",
    "Show Detail",
    {
      link: internalSheetLink(DETAILS_OF_CSR_PROJECT_SHEET),
      valueAlign: "center",
      valueVertical: "middle",
    }
  );

  return ws;
}
